import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { useStorageUpload, uploadedFileFields } from '../../lib/upload'
import { formatDate } from '../../lib/format'
import type { WorkPermit } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, DocIcon, PlusIcon } from '../../components/icons'

interface PermitsResponse { ok: boolean; items: WorkPermit[]; permit_types: string[] }

export default function Permits() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [showForm, setShowForm] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useStorageUpload(`work_permits/${user?.org_id ?? ''}`)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'permits'],
    queryFn: () => api.get<PermitsResponse>('/ops/permits'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/ops/permits', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'permits'] }); setShowForm(false) },
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.post(`/ops/permits/${id}/status`, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'permits'] }),
  })

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const file = fileRef.current?.files?.[0]
    let fileFields: Record<string, string> = {}
    if (file) {
      try {
        fileFields = uploadedFileFields(await upload.upload(file), 'signed_document')
      } catch {
        return
      }
    }
    create.mutate({
      permit_type: form.get('permit_type'),
      title: form.get('title'),
      location: form.get('location'),
      contractor: form.get('contractor'),
      workers: form.get('workers'),
      valid_from: form.get('valid_from'),
      valid_to: form.get('valid_to'),
      hazards: form.get('hazards'),
      controls: form.get('controls'),
      description: form.get('description'),
      ...fileFields,
    })
  }

  const items = data?.items ?? []
  const types = data?.permit_types?.length ? data.permit_types : ['General Work Permit', 'Other']

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Work permits</h1>
          <p className="page-head__sub">Issue and track permits for high-risk tasks.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Issue permit'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Issue permit'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Issue a work permit" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not issue that permit.'}
                  </Alert>
                )}
                {upload.error && <Alert tone="danger">{upload.error}</Alert>}
                {/* PWA: 4 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Permit', content: (
                      <div className="grid grid--2">
                        <Select label="Permit type" name="permit_type" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {types.map((t) => <option key={t} value={t}>{t}</option>)}
                        </Select>
                        <Input label="Title" name="title" required placeholder="e.g. Hot work — Bay 3" />
                      </div>
                    ) },
                    { title: 'Where & who', content: (
                      <div className="grid grid--2">
                        <Input label="Location" name="location" />
                        <Input label="Contractor" name="contractor" />
                      </div>
                    ) },
                    { title: 'Validity & workers', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Input label="Valid from" name="valid_from" type="date" />
                          <Input label="Valid to" name="valid_to" type="date" />
                        </div>
                        <Input label="Workers involved" name="workers" placeholder="Names or count" />
                      </div>
                    ) },
                    { title: 'Hazards & controls', content: (
                      <div className="stack">
                        <Textarea label="Hazards" name="hazards" />
                        <Textarea label="Controls" name="controls" />
                        <Textarea label="Description" name="description" />
                      </div>
                    ) },
                    { title: 'Signed document', content: (
                      <div className="stack">
                        <Input label="Signed permit (optional)" type="file" ref={fileRef} accept="image/*,application/pdf" />
                        {upload.progress !== null && (
                          <div className="field__hint">Uploading… {upload.progress}%</div>
                        )}
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending || upload.progress !== null} confirm={{ title: "Issue this permit?" }}>Issue permit</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Permits" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<DocIcon size={22} />} title="No permits yet" body="Issued work permits will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Title</th><th>Type</th><th>Valid</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.title || '—'}</td>
                      <td>{p.permit_type || '—'}</td>
                      <td>{p.valid_from ? `${formatDate(p.valid_from)} – ${formatDate(p.valid_to)}` : '—'}</td>
                      <td><StatusBadge status={p.status} /></td>
                      <td className="table__num">
                        {p.status === 'active' && p.id && (
                          <Button size="sm" variant="ghost"
                                  loading={setStatus.isPending}
                                  onClick={async () => {
                                    if (await confirm({ title: 'Close this permit?', message: p.title })) {
                                      setStatus.mutate({ id: p.id!, status: 'closed' })
                                    }
                                  }}>
                            Close
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
